import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import DeleteIcon from "@mui/icons-material/Delete";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Box, Chip, IconButton, Stack, Tooltip } from "@mui/material";
import type { ColDef, ICellRendererParams } from "ag-grid-community";

import { SectionDot } from "@/shared/selectors/ProductionEventSelector";
import type { ShiftInspection } from "../../models/Inspection";
import {
	formatMeasurement,
	formatOutOfSpec,
	formatRevision,
} from "../../utils/reportFormat";

const SectionCell = ({ data, value }: ICellRendererParams<ShiftInspection>) =>
	data ? (
		<Box display="flex" alignItems="center" gap={1}>
			<SectionDot sectionId={data.SectionId} />
			{value}
		</Box>
	) : null;

const OutOfSpecCell = ({ data }: ICellRendererParams<ShiftInspection>) => {
	if (!data) return null;

	const hasTolerances = data.HasTolerances;
	const hasIssues = hasTolerances && data.OutOfSpecCount > 0;
	return (
		<Box sx={{ alignItems: "center", display: "flex", height: "100%" }}>
			<Chip
				size="small"
				label={formatOutOfSpec({
					outOfSpecCount: data.OutOfSpecCount,
					hasTolerances,
				})}
				color={hasIssues ? "error" : hasTolerances ? "success" : "warning"}
				variant={hasIssues || !hasTolerances ? "filled" : "outlined"}
				sx={{ fontSize: "11px", fontWeight: 700, height: 20 }}
			/>
		</Box>
	);
};

export const useTableData = ({
	openInspection,
	deleteInspection,
}: {
	openInspection: (inspection: ShiftInspection) => void;
	deleteInspection: (inspection: ShiftInspection) => void;
}) => {
	const columnDefs: ColDef<ShiftInspection>[] = [
		{
			colId: "inspection",
			field: "ProfileInspectionId",
			headerName: "Sample",
			flex: 0.6,
			valueFormatter: ({ value }) => `#${value}`,
		},
		{
			colId: "inspectedAt",
			field: "InspectedAt",
			headerName: "Sampled at",
			flex: 1.2,
			minWidth: 130,
			valueFormatter: ({ value }) =>
				dxtToLocalServerTime(value, "MM/dd/yy HH:mm") || "-",
		},
		{
			colId: "section",
			field: "SectionName",
			headerName: "Section",
			flex: 1,
			cellRenderer: SectionCell,
		},
		{
			colId: "spec",
			headerName: "Revision",
			flex: 0.8,
			valueGetter: ({ data }) =>
				data ? formatRevision(data.SpecRev, data.SpecSubRev) : "-",
		},
		{
			colId: "heat",
			field: "HeatName",
			headerName: "Heat",
			flex: 0.8,
			valueFormatter: ({ value }) => value ?? "-",
			cellStyle: {
				display: "flex",
				justifyContent: "center",
			},
		},
		{
			colId: "inspector",
			field: "Inspector",
			headerName: "Inspector",
			flex: 1.3,
			valueFormatter: ({ value }) => value ?? "-",
			cellStyle: {
				display: "flex",
				justifyContent: "center",
			},
		},
		{
			colId: "weight",
			field: "MeasuredLbsPerFoot",
			headerName: "lb/ft",
			flex: 0.8,
			type: "rightAligned",
			valueFormatter: ({ value }) => formatMeasurement(value),
		},
		{
			colId: "readings",
			field: "MeasuredItemCount",
			headerName: "Readings",
			flex: 0.8,
			type: "rightAligned",
		},
		{
			colId: "issues",
			headerName: "Result",
			flex: 1.3,
			cellRenderer: OutOfSpecCell,
		},
		{
			colId: "comments",
			field: "Comments",
			headerName: "Comments",
			flex: 3,
			tooltipField: "Comments",
			valueFormatter: ({ value }) => value ?? "-",
		},
		{
			colId: "actions",
			headerName: "",
			pinned: "right",
			sortable: false,
			filter: false,
			resizable: false,
			minWidth: 100,
			maxWidth: 100,
			cellRenderer: ({ data }: ICellRendererParams<ShiftInspection>) =>
				data ? (
					<Stack
						direction="row"
						justifyContent="center"
						alignItems="center"
						height="100%"
					>
						<Tooltip title="Open sample">
							<IconButton
								data-test-id={`manager-open-inspection-${data.ProfileInspectionId}`}
								size="small"
								color="primary"
								onClick={() => openInspection(data)}
							>
								<OpenInNewIcon fontSize="small" />
							</IconButton>
						</Tooltip>
						<Tooltip title="Delete sample">
							<IconButton
								data-test-id={`manager-delete-inspection-${data.ProfileInspectionId}`}
								size="small"
								color="error"
								onClick={() => deleteInspection(data)}
							>
								<DeleteIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</Stack>
				) : null,
		},
	];

	return { columnDefs };
};
